import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import {
  ChevronDown, ChevronUp, ExternalLink, FileText,
  TrendingUp, Shield, Award, RefreshCw, Clock,
  AlertCircle, CheckCircle2, Wifi
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCHEMES_HERO = "https://static.prod-images.emergentagent.com/jobs/236e7b77-d75d-43f0-8b66-78eefb5d55fb/images/ab067d100af3c841a3896845eb723a91a71970464c74cd0306ab0346e7a5fcee.jpeg";

const staticSchemes = [
  {
    id: 1,
    icon: Shield,
    category: "SIIDCUL",
    title: "IIE Sigaddi — SIIDCUL Industrial Scheme",
    summary: "Integrated Industrial Estate developed by SIIDCUL adjacent to Kotdwar, Uttarakhand.",
    details: [
      "SIIDCUL (State Infrastructure & Industrial Development Corporation of Uttarakhand) established in 2002.",
      "IIE Sigaddi is a fully planned industrial estate with all essential infrastructure.",
      "Plots allotted on 90-year leasehold basis to eligible industrial units.",
      "EMD: Rs. 2 lakh for plots up to 4,000 sq.mtr; Rs. 4 lakh for 4,000–8,000 sq.mtr.",
      "Processing Fee: Rs. 10,000 for up to 4,000 sq.mtr.",
      "Land Cost Payment: 50% within 30 days; balance in 4 installments over 2 years.",
      "Corner plot or plots on 45m+ wide road attract 5% location charges.",
      "Mortgage to financial institutions possible after 25% payment.",
      "5% rebate on land cost if 100% paid at allotment.",
    ],
    color: "blue",
  },
  {
    id: 2,
    icon: Award,
    category: "SIDA",
    title: "SIDA — State Industrial Development Authority",
    summary: "SIDA oversees regulated industrial development and provides site map approvals in Uttarakhand.",
    details: [
      "SIDA (State Industrial Development Authority) regulates all notified industrial areas and estates.",
      "Provides integrated planning for industrial, commercial, residential, and recreational facilities.",
      "IIE Sigaddi has obtained SIDA site map approval — ensuring legal compliance.",
      "SIDA ensures sustainable and environmentally responsible industrial development.",
      "Facilitates single-window clearance for industrial units.",
      "Development work actively in progress at IIE Sigaddi.",
      "Allotment of plots on-the-spot basis available.",
    ],
    link: "https://sidauk.in",
    linkLabel: "Visit SIDA Official Site",
    color: "green",
  },
  {
    id: 3,
    icon: TrendingUp,
    category: "State Policy",
    title: "Uttarakhand Industrial Policy 2022–2027",
    summary: "State-level policy framework to promote industrialization and investment in Uttarakhand.",
    details: [
      "Capital Subsidy: Up to 15% (max Rs. 15 lakh) on fixed investment in factory building, plant & machinery.",
      "Women Entrepreneurs: Enhanced capital subsidy of 25% (max Rs. 25 lakh).",
      "Interest Subsidy: 5% per annum (up to Rs. 3 lakh) on term loans from scheduled commercial banks.",
      "Women Entrepreneurs Interest Subsidy: 6% (up to Rs. 5 lakh/year).",
      "Stamp Duty Exemption available for new industrial units.",
      "Units at IIE Sigaddi are eligible for all state government incentives.",
      "Priority sector status for manufacturing, food processing, and green energy.",
    ],
    link: "https://investuttarakhand.uk.gov.in",
    linkLabel: "Invest Uttarakhand Portal",
    color: "orange",
  },
  {
    id: 4,
    icon: Award,
    category: "Mega Policy",
    title: "Uttarakhand Mega Industrial & Investment Policy 2025",
    summary: "Designed to attract large-scale manufacturing enterprises with significant financial incentives.",
    details: [
      "Applicable for Large enterprises (Rs. 50–200 Cr), Mega (Rs. 200–500 Cr), Super Mega (Rs. 500–1,000 Cr).",
      "Capital Subsidy: 10–20% depending on investment category.",
      "Stamp duty reimbursement: Up to Rs. 50 lakhs.",
      "Additional hill incentives: 1–2% in designated hill districts.",
      "Applicable for all manufacturing sectors including auto, food processing, pharma, textiles.",
      "Single-window clearance for all statutory approvals.",
    ],
    link: "https://investuttarakhand.uk.gov.in",
    linkLabel: "Read Mega Policy Details",
    color: "purple",
  },
  {
    id: 5,
    icon: FileText,
    category: "MSME",
    title: "Uttarakhand MSME Policy 2023",
    summary: "Special policy for Micro, Small and Medium Enterprises promoting startups and local industry.",
    details: [
      "Focus on startups, green energy, and industries based on local raw materials.",
      "Priority given to food processing, herbal products, handicrafts, and tourism-linked industries.",
      "Subsidized land allotment for MSME units in IIEs.",
      "Credit Guarantee schemes through CGTMSE for collateral-free lending.",
      "Incubation support and mentorship for new entrepreneurs.",
      "Special provisions for SC/ST and women-led enterprises.",
    ],
    link: "https://complinity.com/legal-update/uttarakhand-msme-policy-10918/",
    linkLabel: "Read MSME Policy Details",
    color: "teal",
  },
];

const colorMap = {
  blue: { bg: "bg-blue-500/10", border: "border-blue-500/30", icon: "text-blue-400", badge: "bg-blue-500/20 text-blue-300" },
  green: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: "text-emerald-400", badge: "bg-emerald-500/20 text-emerald-300" },
  orange: { bg: "bg-orange-500/10", border: "border-orange-500/30", icon: "text-orange-400", badge: "bg-orange-500/20 text-orange-300" },
  purple: { bg: "bg-purple-500/10", border: "border-purple-500/30", icon: "text-purple-400", badge: "bg-purple-500/20 text-purple-300" },
  teal: { bg: "bg-teal-500/10", border: "border-teal-500/30", icon: "text-teal-400", badge: "bg-teal-500/20 text-teal-300" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

export default function Schemes() {
  const [openId, setOpenId] = useState(1);
  const [liveSchemes, setLiveSchemes] = useState([]);
  const [loadingLive, setLoadingLive] = useState(true);
  const [openLiveId, setOpenLiveId] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    axios.get(`${API}/schemes/live`)
      .then((r) => { setLiveSchemes(r.data); setLoadingLive(false); })
      .catch(() => setLoadingLive(false));
  }, []);

  const successSchemes = liveSchemes.filter((s) => s.fetch_status === "success" || s.fetch_status === "curated");

  return (
    <div className="bg-[#0F172A]">
      <PageSEO page="schemes" />
      <Navbar />

      {/* Header */}
      <PageHero
        image={SCHEMES_HERO}
        tag="Govt Schemes & Policies"
        title={t("schemes.title")}
        subtitle={t("schemes.subtitle")}
        breadcrumb={{ home: t("nav.home"), current: t("schemes.breadcrumb") }}
      />

      {/* Stats bar */}
      <div className="bg-[#1E293B] py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { val: "15%", label: t("schemes.capitalSubsidy") },
              { val: "5%", label: t("schemes.interestSubsidy") },
              { val: "90 Yr", label: t("schemes.leaseholdPeriod") },
              { val: "5%", label: t("schemes.rebateFullPay") },
            ].map((item) => (
              <div key={item.label} className="text-center border border-white/10 py-5 px-3">
                <div className="text-orange-500 font-bold text-2xl" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.val}
                </div>
                <div className="text-slate-400 text-xs mt-1">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== LIVE POLICY UPDATES — AI fetched from govt sites ===== */}
      <div className="bg-slate-900 py-10" data-testid="live-schemes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Wifi className="w-5 h-5 text-orange-400" />
              <div>
                <h2
                  className="text-white font-bold uppercase"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.6rem", letterSpacing: "0.04em" }}
                >
                  {t("schemes.liveUpdates")}
                </h2>
                <p className="text-slate-400 text-xs">{t("schemes.liveSubtitle")}</p>
              </div>
            </div>
            {successSchemes.length > 0 && (
              <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-400">
                <RefreshCw className="w-3.5 h-3.5 text-emerald-400" />
                <span>Weekly auto-update: Every Sunday 6 AM</span>
              </div>
            )}
          </div>

          {loadingLive ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-slate-800 animate-pulse h-36 border border-slate-700" />
              ))}
            </div>
          ) : successSchemes.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Clock className="w-10 h-10 mx-auto mb-3 text-slate-600" />
              <p className="font-medium text-sm">{t("schemes.fetching")}</p>
              <p className="text-xs mt-1 text-slate-500">{t("schemes.fetchingSub")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {successSchemes.map((scheme) => (
                <div
                  key={scheme.id}
                  className="bg-slate-800 border border-slate-700 overflow-hidden"
                  data-testid={`live-scheme-${scheme.category}`}
                >
                  <button
                    className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-slate-750 transition-colors"
                    onClick={() => setOpenLiveId(openLiveId === scheme.id ? null : scheme.id)}
                    data-testid={`live-scheme-toggle-${scheme.category}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-xs font-bold bg-orange-500/20 text-orange-400 px-2 py-0.5">
                          {scheme.category}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-emerald-400">
                          <CheckCircle2 className="w-3 h-3" />
                          {scheme.fetch_status === "success" ? "Live" : "Official"}
                        </span>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Updated: {formatDate(scheme.updated_at)}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold text-sm md:text-base"
                        style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.01em" }}>
                        {scheme.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <a
                        href={scheme.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-orange-400 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      {openLiveId === scheme.id ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </button>

                  <div className={`accordion-content ${openLiveId === scheme.id ? "open" : ""}`}>
                    <div className="px-5 pb-5 border-t border-slate-700">
                      <div className="mt-4 text-slate-300 text-sm leading-relaxed whitespace-pre-line">
                        {scheme.summary}
                      </div>
                      <a
                        href={scheme.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 text-orange-400 text-xs font-semibold hover:text-orange-300 transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> {t("schemes.visitOfficial")}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ===== Static Scheme Details — Accordion ===== */}
      <div className="bg-[#0F172A] py-12" data-testid="schemes-accordion">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2
              className="text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: "2rem", textTransform: "uppercase" }}
            >
              {t("schemes.detailedGuide")}
            </h2>
            <p className="text-slate-400 text-sm mt-1">{t("schemes.guideSubtitle")}</p>
          </div>

          <div className="space-y-3">
            {staticSchemes.map((scheme) => {
              const colors = colorMap[scheme.color];
              const isOpen = openId === scheme.id;
              return (
                <div
                  key={scheme.id}
                  className={`border ${isOpen ? colors.border : "border-white/10"} bg-[#1E293B] overflow-hidden`}
                  data-testid={`scheme-item-${scheme.id}`}
                >
                  <button
                    className="w-full px-6 py-4 flex items-center gap-4 text-left hover:bg-white/[0.03] transition-colors"
                    onClick={() => setOpenId(isOpen ? null : scheme.id)}
                    data-testid={`scheme-toggle-${scheme.id}`}
                  >
                    <div className={`w-10 h-10 ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                      <scheme.icon className={`w-5 h-5 ${colors.icon}`} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-xs font-bold px-2 py-0.5 ${colors.badge}`}>{scheme.category}</span>
                      </div>
                      <h3 className="text-white font-semibold text-sm md:text-base"
                        style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.01em" }}>
                        {scheme.title}
                      </h3>
                      <p className="text-slate-500 text-xs mt-0.5 hidden md:block">{scheme.summary}</p>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                  </button>

                  <div className={`accordion-content ${isOpen ? "open" : ""}`}>
                    <div className="px-6 pb-5 border-t border-white/[0.06]">
                      <ul className="mt-4 space-y-2">
                        {scheme.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                      {scheme.link && (
                        <a
                          href={scheme.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-1.5 text-orange-500 text-sm font-semibold hover:text-orange-400 transition-colors"
                          data-testid={`scheme-link-${scheme.id}`}
                        >
                          <ExternalLink className="w-4 h-4" /> {scheme.linkLabel}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-white mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: "1.8rem", textTransform: "uppercase" }}>
            {t("schemes.needHelp")}
          </h3>
          <p className="text-slate-300 text-sm mb-6">{t("schemes.helpDesc")}</p>
          <a href="/contact"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm transition-colors"
            data-testid="schemes-contact-btn">
            {t("schemes.freeConsultation")}
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
